import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Wrench, ExternalLink, Search, Copy, Star, BookOpen, PenLine, MessageSquare, BarChart3, ShieldCheck, FolderTree, GitBranch, Feather, Printer, Sparkles } from "lucide-react";

const CAT_META = {
  "Reference & Citation":  { icon: BookOpen,   accent: "from-amber-100 to-amber-50",   badge: "bg-amber-500/10 text-amber-800" },
  "Writing & Formatting":  { icon: PenLine,    accent: "from-emerald-100 to-emerald-50", badge: "bg-emerald-500/10 text-emerald-800" },
  "Literature Review":     { icon: Search,     accent: "from-sky-100 to-sky-50",       badge: "bg-sky-500/10 text-sky-800" },
  "Writing Assistance":    { icon: MessageSquare, accent: "from-violet-100 to-violet-50", badge: "bg-violet-500/10 text-violet-800" },
  "Data & Figures":        { icon: BarChart3,  accent: "from-rose-100 to-rose-50",     badge: "bg-rose-500/10 text-rose-800" },
  "Plagiarism":            { icon: ShieldCheck, accent: "from-red-100 to-red-50",       badge: "bg-red-500/10 text-red-800" },
  "Organisation & Notes":  { icon: FolderTree, accent: "from-teal-100 to-teal-50",     badge: "bg-teal-500/10 text-teal-800" },
  "Version Control":       { icon: GitBranch,  accent: "from-neutral-200 to-neutral-100", badge: "bg-neutral-500/10 text-neutral-800" },
};
const DEFAULT_META = { icon: Wrench, accent:"from-primary/10 to-primary/5", badge: "bg-primary/10 text-primary" };

export default function FreeTools() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [freeOnly, setFreeOnly] = useState(true);

  const load = () => {
    const params = {};
    if (cat !== "All") params.category = cat;
    if (q) params.q = q;
    if (freeOnly) params.free_only = true;
    api.get("/free-tools", { params }).then(r => { setItems(r.data.items || []); setCats(r.data.categories || []); });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cat, freeOnly]);

  const copyLink = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch { toast.error("Copy failed"); }
  };

  // Feature the first free tool of the current category as a hero.
  const hero = useMemo(() => items.find(i => i.free) || items[0], [items]);
  const rest = useMemo(() => items.filter(i => i !== hero), [items, hero]);

  return (
    <div className="max-w-6xl mx-auto p-8 print:p-0">
      {/* Magazine masthead */}
      <div className="mb-8 flex items-start justify-between gap-6 print:hidden">
        <div>
          <div className="overline">Curated ecosystem</div>
          <h1 className="font-display text-5xl mt-2 flex items-center gap-3"><Wrench className="w-10 h-10 text-primary"/>Recommended external tools</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">The apps working professors actually use — for citations, drafting, literature review, data, originality, and version control. Free-first, curated, ranked.</p>
        </div>
        <Button variant="outline" className="rounded-full" onClick={()=>window.print()} data-testid="ft-print"><Printer className="w-4 h-4 mr-2"/>Print list</Button>
      </div>

      {/* Search + toggle strip */}
      <div className="card-crisp rounded-lg p-4 mb-6 flex flex-wrap items-center gap-3 print:hidden">
        <div className="flex-1 min-w-[240px] flex items-center gap-2 border border-border rounded-full px-3 py-1.5 bg-background">
          <Search className="w-4 h-4 text-muted-foreground"/>
          <input className="flex-1 bg-transparent outline-none text-sm" placeholder="Search zotero, LaTeX, R, plagiarism…" value={q} onChange={(e)=>setQ(e.target.value)} onKeyDown={(e)=>e.key==="Enter"&&load()} data-testid="ft-search"/>
        </div>
        <Button variant="ghost" onClick={load} className="rounded-full">Search</Button>
        <div className="flex items-center gap-2 text-sm">
          <Sparkles className="w-4 h-4 text-primary"/>Free only
          <Switch id="freeOnly" checked={freeOnly} onCheckedChange={setFreeOnly} data-testid="ft-free-only"/>
        </div>
      </div>

      {/* Category chips as a horizontal magazine rail */}
      <div className="flex flex-wrap gap-2 mb-8 print:hidden" data-testid="ft-categories">
        {["All", ...cats].map(c => {
          const meta = CAT_META[c] || DEFAULT_META;
          const Icon = meta.icon;
          const active = cat === c;
          return (
            <button
              key={c}
              onClick={()=>setCat(c)}
              className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-xs transition-all border ${active ? "bg-primary text-primary-foreground border-primary shadow" : "bg-card border-border hover:bg-secondary hover:-translate-y-0.5"}`}
              data-testid={`ft-cat-${c.replace(/\s|&/g,'_')}`}
            >
              <Icon className="w-3.5 h-3.5"/>
              {c}
            </button>
          );
        })}
      </div>

      {/* Featured hero */}
      {hero && (
        <FeatureHero item={hero} onOpen={()=>window.open(hero.url,"_blank")} onCopy={()=>copyLink(hero.url)}/>
      )}

      {/* Rest of the grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
        {rest.map(it => (
          <ToolCard key={it.name} it={it} onCopy={()=>copyLink(it.url)}/>
        ))}
      </div>

      {items.length === 0 && (
        <div className="card-crisp rounded-lg p-10 text-center">
          <Wrench className="w-10 h-10 text-primary mx-auto mb-3"/>
          <div className="font-display text-2xl">Nothing matches yet</div>
          <p className="text-muted-foreground mt-1">Try a different search term or clear the "Free only" filter.</p>
        </div>
      )}

      {/* Editorial closing note */}
      <div className="mt-10 rounded-lg border-l-4 border-primary bg-primary/5 p-5 print:break-inside-avoid">
        <div className="overline text-primary">Editor's note</div>
        <div className="font-display text-2xl mt-1">Build your own stack</div>
        <p className="text-sm text-muted-foreground mt-2">STEM tends towards LaTeX (Overleaf) + Zotero + Python. Humanities lean Word/Scrivener + Zotero + Grammarly. Combine any of these with GrantForge's drafting, originality and analyser modules for a full research workflow.</p>
      </div>
    </div>
  );
}

function FeatureHero({ item, onOpen, onCopy }) {
  const meta = CAT_META[item.category] || DEFAULT_META;
  const Icon = meta.icon;
  return (
    <div className={`rounded-xl border border-primary/30 overflow-hidden shadow-sm bg-gradient-to-br ${meta.accent} print:break-inside-avoid`} data-testid={`ft-hero-${item.name.replace(/\s/g,'_')}`}>
      <div className="p-6 md:p-8 flex flex-col md:flex-row gap-6 items-start">
        <div className="w-16 h-16 rounded-2xl bg-white/70 backdrop-blur flex items-center justify-center border border-white">
          <Icon className="w-8 h-8 text-primary"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="overline">Editor's pick · {item.category}</span>
            <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${item.free ? "bg-emerald-500/10 text-emerald-800" : "bg-amber-500/10 text-amber-800"}`}>{item.free ? "Free" : "Paid"}</span>
          </div>
          <div className="font-display text-4xl mt-1 leading-tight">{item.name}</div>
          <p className="text-sm text-foreground/80 mt-3 max-w-2xl">{item.summary}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {item.best_for && <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary flex items-center gap-1"><Star className="w-3 h-3"/>{item.best_for}</span>}
            {item.fit && <span className="text-xs px-2 py-1 rounded-full bg-white/70">{item.fit}</span>}
          </div>
          <div className="mt-5 flex gap-2">
            <Button onClick={onOpen} className="rounded-full" data-testid={`ft-open-${item.name.replace(/\s/g,'_')}`}><ExternalLink className="w-4 h-4 mr-1"/>Open {item.name}</Button>
            <Button variant="outline" onClick={onCopy} className="rounded-full"><Copy className="w-4 h-4 mr-1"/>Copy link</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ToolCard({ it, onCopy }) {
  const meta = CAT_META[it.category] || DEFAULT_META;
  const Icon = meta.icon;
  return (
    <a href={it.url} target="_blank" rel="noreferrer" className="group card-crisp rounded-lg p-5 flex flex-col hover:border-primary/60 hover:-translate-y-0.5 transition-all print:break-inside-avoid" data-testid={`ft-item-${it.name.replace(/\s/g,'_')}`}>
      <div className="flex items-start justify-between gap-2">
        <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${meta.badge}`}>
          <Icon className="w-5 h-5"/>
        </div>
        <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${it.free ? "bg-emerald-500/10 text-emerald-800" : "bg-amber-500/10 text-amber-800"}`}>
          {it.free ? "Free" : "Paid"}
        </span>
      </div>
      <div className="font-display text-2xl mt-3 leading-tight group-hover:text-primary">{it.name}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{it.category}</div>
      <p className="text-sm mt-3 flex-1">{it.summary}</p>
      <div className="mt-3 flex flex-wrap gap-1">
        {it.fit && <span className="text-[11px] px-2 py-0.5 rounded-full bg-secondary">{it.fit}</span>}
        {it.best_for && <span className="text-[11px] px-2 py-0.5 rounded-full bg-primary/10 text-primary"><Star className="inline w-3 h-3 mr-0.5"/>{it.best_for}</span>}
      </div>
      <div className="mt-4 flex gap-2 items-center">
        <span className="text-primary text-sm inline-flex items-center gap-1 group-hover:underline"><ExternalLink className="w-3.5 h-3.5"/>Open tool</span>
        <button onClick={(e)=>{e.preventDefault(); onCopy();}} className="ml-auto text-muted-foreground hover:text-primary text-xs inline-flex items-center gap-1" data-testid={`ft-copy-${it.name.replace(/\s/g,'_')}`}><Copy className="w-3 h-3"/>Copy</button>
      </div>
    </a>
  );
}
